import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs, doc, setDoc } from 'firebase/firestore';
import { updateConceptMastery } from './masteryService';

import { safeStringify } from '../lib/utils';

export interface TutorMessage {
  role: 'user' | 'model';
  text: string;
}

export interface TutorQueryContext {
  userId: string;
  courseId: string;
  lessonId: string;
  user_query: string;
  lesson_content: string;
  current_level: string;
  concepts: any[];
  source_excerpts: string[];
  quizPerformance?: any[];
  visuals?: any[];
  onToken?: (token: string) => void;
}

async function callTutorApi(history: any[], message: string, context?: any) {
  const response = await fetch('/api/tutor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: safeStringify({ history, message, context })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Tutor API failed');
  }
  const envelope = await response.json();
  if (!envelope.success) {
    throw new Error(envelope.error || 'Tutor API failed');
  }
  return envelope.data;
}

export async function handleTutorQuery(context: TutorQueryContext): Promise<{ response: string; intent: string }> {
  const { userId, courseId, lessonId, user_query, onToken } = context;

  // 1. Manage/Load History and Enhanced Memory from Firestore
  const sessionId = `${userId}_${courseId}_${lessonId}`;
  const history = await loadChatHistory(sessionId);
  
  // 2. Intent Detection
  const intent = detectIntent(user_query);

  try {
    // 3. Call Server API
    const apiRes = await callTutorApi(history, user_query, context);
    const fullResponse = apiRes.response;

    if (onToken) {
      onToken(fullResponse);
    }

    // 4. Save To Firestore
    await saveChatMessage(sessionId, userId, courseId, lessonId, user_query, fullResponse, intent);

    return { response: fullResponse, intent };
  } catch (error) {
    console.error('Tutor Service Error:', error);
    throw error;
  }
}

function detectIntent(query: string): 'explain' | 'simplify' | 'example' | 'quiz' | 'default' {
  const q = query.toLowerCase();
  if (q.includes('explain') || q.includes('how') || q.includes('why')) return 'explain';
  if (q.includes('simplify') || q.includes('basic') || q.includes('easy')) return 'simplify';
  if (q.includes('example') || q.includes('analogy')) return 'example';
  if (q.includes('quiz') || q.includes('test') || q.includes('question')) return 'quiz';
  return 'default';
}

async function loadChatHistory(sessionId: string): Promise<TutorMessage[]> {
  try {
    const messagesRef = collection(db, 'chat_sessions', sessionId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(5));
    const snapshot = await getDocs(q);
    
    return snapshot.docs
      .map(doc => ({ role: doc.data().role, text: doc.data().text } as TutorMessage))
      .reverse();
  } catch (error) {
    console.warn('Failed to load history:', error);
    return [];
  }
}

async function saveChatMessage(
  sessionId: string, 
  userId: string, 
  courseId: string, 
  lessonId: string, 
  user_query: string, 
  tutor_response: string,
  intent: string
) {
  try {
    const sessionRef = doc(db, 'chat_sessions', sessionId);
    await setDoc(sessionRef, {
      userId,
      courseId,
      lessonId,
      lastMessageAt: serverTimestamp()
    }, { merge: true });

    const messagesRef = collection(db, 'chat_sessions', sessionId, 'messages');
    
    // Save user message
    await addDoc(messagesRef, {
      role: 'user',
      text: user_query,
      intent,
      createdAt: serverTimestamp()
    });

    // Save model message
    await addDoc(messagesRef, {
      role: 'model',
      text: tutor_response,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Failed to save message:', error);
  }
}
